import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Bell, X, RefreshCw, ArrowRight, ArrowUpDown, Settings2, RotateCcw, Plus, GripVertical, Ship } from "lucide-react";
import { api } from "../lib/api.js";
import { useI18n } from "../lib/i18n.jsx";
import { fmtDateTime, fmtRelative } from "../lib/format.js";
import { useAuth } from "../lib/auth.jsx";
import ConfigurableTable, { ColumnConfigButton, useColumnConfig } from "../components/ConfigurableTable.jsx";
import SavedViewsBar from "../components/SavedViewsBar.jsx";
import MyCasesPanel from "../components/MyCasesPanel.jsx";
import AnalyticsPanel from "../components/AnalyticsPanel.jsx";

const COLOR_CLASS = {
  red: "border-l-[#DC3545] text-[#721C24]",
  yellow: "border-l-[#FFC107] text-[#856404]",
  grey: "border-l-[#6C757D] text-[#495057]",
  green: "border-l-[#28A745] text-[#155724]",
};

const NUMBER_COLOR = {
  red: "text-[#DC3545]",
  yellow: "text-[#B45309]",
  grey: "text-[#6C757D]",
  green: "text-[#28A745]",
};

function formatDelay(hours) {
  if (hours == null || isNaN(hours)) return "—";
  if (hours < 0) hours = 0;
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const days = Math.floor(hours / 24);
  const remH = Math.round(hours - days * 24);
  return `${days} dia${days === 1 ? "" : "s"} ${remH}h`;
}

function ExceptionCard({ card, onOpen, editMode, onHide, dragHandleProps }) {
  return (
    <div
      data-testid={`exception-card-${card.key}`}
      className={`relative text-left bg-white border border-slate-200 border-l-[3px] ${COLOR_CLASS[card.color] || COLOR_CLASS.grey} rounded-lg p-4 transition-all shadow-soft-card ${
        editMode ? "ring-2 ring-ronil-navy/30" : "hover:border-ronil-navy hover:shadow-elevation hover:-translate-y-0.5 cursor-pointer"
      }`}
      {...dragHandleProps}
    >
      {editMode && (
        <button
          onClick={(e) => { e.stopPropagation(); onHide(card.id); }}
          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm z-10"
          data-testid={`hide-card-${card.id}`}
          aria-label={`Esconder ${card.label}`}
          title="Esconder card"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      <button
        type="button"
        onClick={() => !editMode && onOpen(card)}
        className="w-full text-left"
        disabled={editMode}
        data-testid={`exception-card-btn-${card.key}`}
      >
        <div className="flex items-start justify-between mb-3">
          <span className="text-[9px] font-mono font-bold text-slate-400 tracking-widest">#{card.id.toString().padStart(2, "0")}</span>
          {editMode ? (
            <GripVertical className="w-3.5 h-3.5 text-slate-400" />
          ) : (
            <Bell className="w-3 h-3 text-slate-400" />
          )}
        </div>
        <div className={`font-heading text-4xl font-bold tabular-nums ${NUMBER_COLOR[card.color] || "text-slate-900"} leading-none tracking-tight`} data-testid={`exception-card-count-${card.key}`}>
          {card.count}
        </div>
        <div className="mt-3 text-[11px] font-heading font-semibold text-slate-700 uppercase tracking-wider leading-snug line-clamp-2">
          {card.label}
        </div>
      </button>
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isManager = ["admin", "national_aftersales_manager", "department_manager", "workshop_supervisor"].includes(user?.role);
  const [data, setData] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [_, forceTick] = useState(0);
  // Customize
  const [editMode, setEditMode] = useState(false);
  const [prefs, setPrefs] = useState({ card_order: [], hidden_cards: [] });
  const [dragId, setDragId] = useState(null);
  const [activeTab, setActiveTab] = useState("exceptions");

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/dashboard/exceptions");
      setData(r.data);
      setLastRefreshed(new Date());
      if (activeCard) {
        const updated = r.data.cards.find((c) => c.id === activeCard.id);
        if (updated) setActiveCard(updated);
      }
    } finally {
      setRefreshing(false);
    }
  }, [activeCard]);

  const loadPrefs = useCallback(async () => {
    const r = await api.get("/users/me/dashboard-prefs");
    setPrefs(r.data);
  }, []);

  useEffect(() => {
    fetchData();
    loadPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    const { data: d } = await api.put("/users/me/dashboard-prefs", next);
    setPrefs(d);
  };

  const orderedCards = useMemo(() => {
    if (!data) return [];
    const byId = new Map(data.cards.map((c) => [c.id, c]));
    const order = prefs.card_order && prefs.card_order.length ? prefs.card_order : data.cards.map((c) => c.id);
    const seen = new Set();
    const out = [];
    for (const id of order) {
      const c = byId.get(id);
      if (c && !seen.has(id)) { out.push(c); seen.add(id); }
    }
    // Append any new card ids not in saved order (e.g. backend added new card)
    for (const c of data.cards) {
      if (!seen.has(c.id)) { out.push(c); seen.add(c.id); }
    }
    return out;
  }, [data, prefs]);

  const visibleCards = useMemo(() => orderedCards.filter((c) => !prefs.hidden_cards?.includes(c.id) && !c.placeholder), [orderedCards, prefs]);
  const hiddenCards = useMemo(() => orderedCards.filter((c) => prefs.hidden_cards?.includes(c.id) && !c.placeholder), [orderedCards, prefs]);

  const onHideCard = async (cardId) => {
    const hidden = Array.from(new Set([...(prefs.hidden_cards || []), cardId]));
    await savePrefs({ card_order: prefs.card_order, hidden_cards: hidden });
  };

  const onAddCard = async (cardId) => {
    const hidden = (prefs.hidden_cards || []).filter((x) => x !== cardId);
    await savePrefs({ card_order: prefs.card_order, hidden_cards: hidden });
  };

  const onResetPrefs = async () => {
    const { data: d } = await api.delete("/users/me/dashboard-prefs");
    setPrefs(d);
  };

  // ----- HTML5 drag handlers -----
  const handleDragStart = (e, cardId) => {
    if (!editMode) return;
    setDragId(cardId);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(cardId)); } catch (_) { /* noop */ }
  };
  const handleDragOver = (e) => { if (editMode) e.preventDefault(); };
  const handleDropOn = async (e, targetCardId) => {
    if (!editMode) return;
    e.preventDefault();
    const sourceId = dragId ?? Number(e.dataTransfer.getData("text/plain"));
    if (!sourceId || sourceId === targetCardId) { setDragId(null); return; }
    const current = visibleCards.map((c) => c.id);
    const srcIdx = current.indexOf(sourceId);
    const tgtIdx = current.indexOf(targetCardId);
    if (srcIdx < 0 || tgtIdx < 0) { setDragId(null); return; }
    current.splice(srcIdx, 1);
    current.splice(tgtIdx, 0, sourceId);
    // Merge with hidden cards (keep them at end in same relative order)
    const hidden = orderedCards.filter((c) => prefs.hidden_cards?.includes(c.id)).map((c) => c.id);
    const fullOrder = [...current, ...hidden];
    setDragId(null);
    await savePrefs({ card_order: fullOrder, hidden_cards: prefs.hidden_cards || [] });
  };

  if (!data) {
    return <div className="p-6 text-sm text-[#6C757D]">A carregar…</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-ronil-sand font-heading font-bold mb-2">
            Painel executivo
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ronil-navy leading-[1.1]" data-testid="dashboard-title">
            {t("exception_dashboard")}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">{t("exception_subtitle")}</p>
        </div>
        <div className="flex gap-2.5 items-center flex-wrap">
          <Stat label={t("open_cases")} value={data.stats.open_cases} testid="stat-open-cases" />
          <Stat label={t("open_prs")} value={data.stats.open_parts_requests} testid="stat-open-prs" />
          <Stat label={t("open_tasks")} value={data.stats.open_tasks} testid="stat-open-tasks" />
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 text-xs text-[#6C757D]">
        <div data-testid="last-refreshed">
          {lastRefreshed ? (
            <>
              Última actualização: <span className="font-medium text-[#1A1A2E]">{fmtDateTime(lastRefreshed.toISOString())}</span>
              <span className="ml-1.5 text-[#94A3B8]">({fmtRelative(lastRefreshed.toISOString())})</span>
            </>
          ) : "—"}
        </div>
        <div className="flex items-center gap-2">
          {editMode && (
            <>
              <button
                onClick={onResetPrefs}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-sm hover:border-[#1F3864] hover:text-[#1F3864] transition-colors"
                data-testid="reset-dashboard-prefs"
              >
                <RotateCcw className="w-3 h-3" /> Repor predefinição
              </button>
              <button
                onClick={() => setEditMode(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#28A745] hover:bg-[#1f8c38] text-white rounded-sm transition-colors"
                data-testid="exit-customize-mode"
              >
                Concluído
              </button>
            </>
          )}
          {!editMode && (
            <>
              <button
                data-testid="customize-dashboard"
                onClick={() => setEditMode(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-sm hover:border-[#1F3864] hover:text-[#1F3864] transition-colors"
              >
                <Settings2 className="w-3.5 h-3.5" />
                <span className="font-medium">Personalizar painel</span>
              </button>
              <button
                data-testid="refresh-dashboard"
                onClick={fetchData}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-sm hover:border-[#1F3864] hover:text-[#1F3864] transition-colors disabled:opacity-60"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                <span className="font-medium">Actualizar</span>
              </button>
            </>
          )}
        </div>
      </div>

      {editMode && (
        <div className="mb-3 px-3 py-2 bg-[#FFF7E6] border border-[#FFC107]/40 text-[#856404] text-xs rounded-sm" data-testid="customize-banner">
          Arrasta os cards para reordenar. Clica no <b>×</b> vermelho para esconder. Cards escondidos aparecem no painel lateral — clica para os voltar a mostrar.
        </div>
      )}

      {/* Tabs: Excepções | Minhas obras */}
      <div className="mb-4 flex items-center gap-1 border-b border-gray-200" data-testid="dashboard-tabs">
        <button
          onClick={() => setActiveTab("exceptions")}
          className={`px-4 py-2 text-sm font-semibold tracking-wide border-b-2 -mb-px transition ${activeTab === "exceptions" ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"}`}
          data-testid="dashboard-tab-exceptions"
        >
          Excepções
        </button>
        <button
          onClick={() => setActiveTab("my-cases")}
          className={`px-4 py-2 text-sm font-semibold tracking-wide border-b-2 -mb-px transition ${activeTab === "my-cases" ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"}`}
          data-testid="dashboard-tab-my-cases"
        >
          Minhas obras
        </button>
        {isManager && (
          <button
            onClick={() => setActiveTab("analytics")}
            className={`px-4 py-2 text-sm font-semibold tracking-wide border-b-2 -mb-px transition ${activeTab === "analytics" ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"}`}
            data-testid="dashboard-tab-analytics"
          >
            Análise
          </button>
        )}
      </div>

      {activeTab === "my-cases" ? (
        <MyCasesPanel />
      ) : activeTab === "analytics" && isManager ? (
        <AnalyticsPanel />
      ) : (
      <div className="flex gap-3">
        <div
          data-testid="exception-cards"
          className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 flex-1`}
        >
          {visibleCards.map((c) => (
            <div
              key={c.id}
              draggable={editMode}
              onDragStart={(e) => handleDragStart(e, c.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDropOn(e, c.id)}
              className={dragId === c.id ? "opacity-50" : ""}
            >
              <ExceptionCard
                card={c}
                onOpen={setActiveCard}
                editMode={editMode}
                onHide={onHideCard}
              />
            </div>
          ))}
        </div>

        {editMode && (
          <aside className="w-60 bg-white border border-gray-200 rounded-md p-3 h-fit sticky top-20" data-testid="hidden-cards-panel">
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-2">Cards escondidos</div>
            {hiddenCards.length === 0 ? (
              <div className="text-xs text-[#94A3B8]">Todos os cards visíveis.</div>
            ) : (
              <ul className="space-y-1.5">
                {hiddenCards.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => onAddCard(c.id)}
                      className="w-full text-left text-xs px-2 py-1.5 bg-[#F4F6F8] hover:bg-[#E2E8F0] rounded-sm flex items-center justify-between gap-2 group"
                      data-testid={`add-card-${c.id}`}
                    >
                      <span className="truncate">
                        <span className="font-mono text-[10px] text-[#94A3B8] mr-1.5">#{c.id.toString().padStart(2, "0")}</span>
                        {c.label}
                      </span>
                      <Plus className="w-3 h-3 text-[#1F3864] shrink-0 opacity-60 group-hover:opacity-100" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}
      </div>
      )}

      {activeCard && (
        <DrillDownPanel card={activeCard} onClose={() => setActiveCard(null)} />
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-2.5 flex flex-col shadow-soft-card" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold">{label}</div>
      <div className="font-heading text-2xl font-bold text-ronil-navy tabular-nums">{value}</div>
    </div>
  );
}

function DrillDownPanel({ card, onClose }) {
  const colConfig = useColumnConfig("dashboard_drilldown");
  const items = card.items || [];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="drill-down-panel" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <div
        className="w-full max-w-6xl bg-white border-l border-gray-200 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Excepção #{card.id.toString().padStart(2, "0")}</div>
            <h2 className="font-heading text-lg font-bold text-[#1A1A2E] truncate" data-testid="drill-down-title">{card.label}</h2>
            <div className="text-xs text-[#6C757D]" data-testid="drill-down-count">
              {card.count} {card.count === 1 ? "item" : "itens"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ColumnConfigButton config={colConfig} viewKey="dashboard_drilldown" />
            <button data-testid="drill-down-close" onClick={onClose} className="p-1.5 hover:bg-[#F4F6F8] rounded-sm">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 py-2 border-b border-gray-100 bg-[#FAFBFC]">
          <SavedViewsBar viewKey="dashboard_drilldown" config={colConfig} />
        </div>

        <div className="flex-1 overflow-auto p-3">
          <ConfigurableTable
            viewKey="dashboard_drilldown"
            config={colConfig}
            rows={items}
            emptyMessage="Sem itens nesta excepção."
          />
        </div>
      </div>
    </div>
  );
}

function DelayCell({ hours, color }) {
  const dotColor = {
    red: "bg-[#DC3545]",
    yellow: "bg-[#FFC107]",
    grey: "bg-[#6C757D]",
    green: "bg-[#28A745]",
  }[color] || "bg-[#6C757D]";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      {formatDelay(hours)}
    </span>
  );
}


// Compact Logistics summary strip shown above the dashboard tabs.
function LogisticsStrip() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/vehicle-logistics/dashboard").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  const cards = [
    { k: "active",      label: "Em curso",         value: d.active,              tone: "bg-slate-50 text-slate-700 border-slate-200" },
    { k: "incomplete",  label: "Workflow incompl.",value: d.workflow_incomplete, tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { k: "blocked",     label: "Bloqueadas",       value: d.blocked,             tone: "bg-rose-50 text-rose-800 border-rose-200" },
    { k: "w7",          label: "Em W7",            value: d.w7?.total || 0,      tone: "bg-slate-50 text-slate-700 border-slate-200", extra: `>14d: ${d.w7?.over_14d || 0} · >30d: ${d.w7?.over_30d || 0}` },
    { k: "ready",       label: "Prontas p/ VR",    value: d.ready_for_vr,        tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    { k: "today",       label: "Transferidas hoje",value: d.transferred_today,   tone: "bg-sky-50 text-sky-800 border-sky-200" },
  ];
  return (
    <section className="mb-4" data-testid="dashboard-logistics-strip">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-[11px] uppercase tracking-widest text-[#1F3864] inline-flex items-center gap-1.5 font-bold">
          <Ship className="w-3.5 h-3.5" /> Logística de Viaturas
        </h3>
        <Link to="/logistica" className="text-[11px] text-[#1F3864] font-heading font-semibold inline-flex items-center gap-0.5 hover:underline">
          Abrir Logística <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {cards.map((c) => (
          <Link
            key={c.k}
            to="/logistica"
            data-testid={`logistics-kpi-${c.k}`}
            className={`block border rounded-md px-3 py-2 transition hover:shadow-sm ${c.tone}`}
          >
            <div className="text-[9px] uppercase tracking-widest font-heading font-semibold opacity-80">{c.label}</div>
            <div className="font-heading font-bold text-xl leading-none tabular-nums mt-1">{c.value}</div>
            {c.extra && <div className="text-[10px] opacity-70 mt-0.5">{c.extra}</div>}
          </Link>
        ))}
      </div>
    </section>
  );
}
